/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpErrorResponse } from '@angular/common/http';
import {
  documentToHtmlString,
  Options
} from '@contentful/rich-text-html-renderer';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { EntryCollection } from 'contentful';
import {
  IAcademyVideosItem,
  ICmsAcademyVideo
} from '../../interfaces/academy-video';
import { ICmsAuthor } from '../../interfaces/cms';
import { IAcademyBookItem } from '../../interfaces/academy-library';
import { IAcademyOfferItem } from '../../interfaces/academy-offers';
import { LegalEntityCountry } from '../../interfaces/legal-entity';

export class AcademyHelper {
  static getAcademyBookItemHandler(
    academyBookEntries: EntryCollection<IAcademyBookItem>,
    sectionRendererOptions: Partial<Options>
  ): IAcademyBookItem {
    const results: Array<IAcademyBookItem> = academyBookEntries.items.map(
      (entry) => {
        return {
          id: entry.sys.id,
          title: entry.fields.title,
          cover: (entry.fields as any)?.cover?.fields?.file?.url
            ? (entry.fields as any)?.cover?.fields?.file?.url
            : null,
          author: {
            id: (entry.fields.author as any)?.sys.id,
            avatar: ((entry.fields.author as any)?.fields as any)?.avatar.fields.file.url,
            fullName: ((entry.fields.author as any)?.fields as ICmsAuthor)?.fullName
          },
          pages: entry.fields.pages,
          categories: entry.fields.categories,
          content: documentToHtmlString(
            (entry.fields as any)?.description as any
          ),
          titleOfTheMonth: entry.fields.titleOfTheMonth,
          attachment: (entry.fields as any)?.attachment?.fields?.file,
          publishDate: new Date((entry.fields as any)?.publishDate as string),
          type: entry.fields.type,
          fileTypes: entry.fields.fileTypes,
          creationDate: new Date((entry.fields as any)?.dateCreated as string),
          genre: entry.fields.genre,
          language: entry.fields.language
        };
      }
    );

    if (results.length === 0) {
      throw new HttpErrorResponse({ status: 404, statusText: 'Not found' });
    }

    return results[0];
  }

  static getAcademyBookListHandler(
    academyBookEntries: EntryCollection<IAcademyBookItem>,
    sectionRendererOptions: Partial<Options>
  ): { books: IAcademyBookItem[]; total: number } {
    const results: Array<IAcademyBookItem> = academyBookEntries.items.map(
      (entry) => {
        return {
          id: entry.sys.id,
          title: entry.fields.title,
          cover: (entry.fields as any)?.cover?.fields?.file?.url
            ? (entry.fields as any)?.cover?.fields?.file?.url
            : null,
          author: {
            id: (entry.fields.author as any)?.sys.id,
            avatar: ((entry.fields.author as any)?.fields as any)?.avatar.fields.file.url,
            fullName: ((entry.fields.author as any)?.fields as ICmsAuthor)?.fullName
          },
          pages: entry.fields.pages,
          categories: entry.fields.categories,
          content: documentToPlainTextString(
            (entry.fields as any)?.description as any
          ),
          titleOfTheMonth: entry.fields.titleOfTheMonth,
          attachment: (entry.fields as any)?.attachment?.fields?.file,
          publishDate: new Date((entry.fields as any)?.publishDate as string),
          type: entry.fields.type,
          fileTypes: entry.fields.fileTypes,
          creationDate: new Date((entry.fields as any)?.dateCreated as string),
          genre: entry.fields.genre,
          language: entry.fields.language
        };
      }
    );

    return { books: results, total: academyBookEntries.total };
  }

  static getAcademyVideosHandler(
    academyVideoEntries: EntryCollection<ICmsAcademyVideo>,
    sectionRendererOptions: Partial<Options>
  ): { videos: Array<IAcademyVideosItem>; total: number } {
    const results: Array<IAcademyVideosItem> = [];

    academyVideoEntries.items.forEach((entry) => {
      const item: IAcademyVideosItem = {
        id: entry.sys.id,
        title: entry.fields.title,
        content: documentToHtmlString(
          entry.fields.description as any,
          sectionRendererOptions
        ),
        headerVideoUrl: entry.fields.videoUrl,
        headerImageUrl: (entry.fields as any)?.videoThumbnail?.fields?.file?.url
          ? (entry.fields as any)?.videoThumbnail?.fields?.file?.url
          : null,
        author: {
          id: (entry.fields.author as any)?.sys.id,
          avatar: ((entry.fields.author as any)?.fields as any)?.avatar.fields.file.url,
          fullName: ((entry.fields.author as any)?.fields as ICmsAuthor)?.fullName
        },
        duration: entry.fields.duration,
        creationDate: new Date((entry.fields as any)?.videoDate as string),
        countries: entry.fields.countries as Array<LegalEntityCountry>,
        isFeatured: entry.fields.isFeatured,
        additionalAssetsUrl: entry.fields.additionalAssetsUrl,
        categories: entry.fields.categories as Array<string>,
        tags: entry.fields.categories as Array<string>,
        videoLanguages: entry.fields.videoLanguages as Array<string>
      };

      results.push(item);
    });

    return { videos: results, total: academyVideoEntries.total };
  }

  static getAcademyOffersHandler(
    academyOffersEntries: EntryCollection<IAcademyOfferItem>,
    sectionRendererOptions: Partial<Options>
  ): { offers: Array<IAcademyOfferItem>; total: number } {
    const results: Array<IAcademyOfferItem> = [];

    academyOffersEntries.items.forEach((entry) => {
      const item: IAcademyOfferItem = {
        id: entry.sys.id,
        content: documentToHtmlString((entry.fields as any)?.description as any),
        creationDate: new Date((entry.fields as any)?.dateCreated as string),
        shortText: entry.fields.shortText,
        videoUrl: entry.fields.videoUrl ?? null,
        videoThumbnail: (entry.fields as any)?.videoThumbnail?.fields?.file?.url
          ? (entry.fields as any)?.videoThumbnail?.fields?.file?.url
          : null,
        partnerData: {
          partnerBrochure: (entry.fields as any)?.partnerBrochure ?? null,
          partnerWebsite: (entry.fields as any)?.partnerWebsite ?? null,
          partnerName: (entry.fields as any)?.partnerName,
          partnerLogo: (entry.fields as any)?.partnerLogo?.fields?.file?.url
            ? (entry.fields as any)?.partnerLogo?.fields?.file?.url
            : null
        },
        accessUrl: entry.fields.accessUrl ?? null,
        accessCode: entry.fields.accessCode ?? null
      };

      results.push(item);
    });

    return { offers: results, total: academyOffersEntries.total };
  }

  static getAcademyOfferHandler(
    academyOffersEntries: EntryCollection<IAcademyOfferItem>,
    sectionRendererOptions: Partial<Options>
  ): IAcademyOfferItem {
    const results: Array<IAcademyOfferItem> = [];

    academyOffersEntries.items.forEach((entry) => {
      const item: IAcademyOfferItem = {
        id: entry.sys.id,
        content: documentToHtmlString((entry.fields as any)?.description as any),
        creationDate: new Date((entry.fields as any)?.dateCreated as string),
        shortText: entry.fields.shortText,
        videoUrl: entry.fields.videoUrl ?? null,
        videoThumbnail: (entry.fields as any)?.videoThumbnail?.fields?.file?.url
          ? (entry.fields as any)?.videoThumbnail?.fields?.file?.url
          : null,
        partnerData: {
          partnerBrochure: (entry.fields as any)?.partnerBrochure ?? null,
          partnerWebsite: (entry.fields as any)?.partnerWebsite ?? null,
          partnerName: (entry.fields as any)?.partnerName,
          partnerLogo: (entry.fields as any)?.partnerLogo?.fields?.file?.url
            ? (entry.fields as any)?.partnerLogo?.fields?.file?.url
            : null
        },
        accessUrl: entry.fields.accessUrl ?? null,
        accessCode: entry.fields.accessCode ?? null
      };

      results.push(item);
    });

    if (results.length === 0) {
      throw new HttpErrorResponse({ status: 404, statusText: 'Not found' });
    }
    return results[0];
  }
}
